import React, { useState, useCallback, memo, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import { emptyArray } from 'ui-components/default-props/empty';
import { HooksDisplayGrid } from 'ui-components/components/hooks-display-grid';
import Global from '../../../../../store/global-selectors';
import { setSelectedBatchesByIds, updateBatchGridSorting } from '../../../../../store/batches/grid/batches.grid.actions';
import { saveUserSettingGridConfig } from '../../../../../store/user/user.actions';
import { shipmentGridChangeColumns } from '../../../../../store/shipments/grid/shipments.grid.actions';

import messages from './messages';
import tabsToPropsMap from './tabs-to-props-map';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';

const statusViewId = 'batches-list';
const defaultTab = 'open';

const BatchGridContainer = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const batches = useSelector(Global.selectBatchesBatchRows) || emptyArray;
  const selectedBatches = useSelector(Global.selectBatchesSelectedBatches) || emptyArray;
  const { tabFilter } = useParams();
  const selectedTab = tabFilter || defaultTab;
  const { columns: columnsBasedOnStatus } = tabsToPropsMap[selectedTab || 'open'];

  const columnConfiguration = useSelector((state) =>
    Global.selectShipmentsGridCurrentViewGridConfigOrDefault(state, {
      statusViewId,
      activeFilterViewId: selectedTab,
    }),
  );

  const translatedColumns = columnsBasedOnStatus.map((column) => {
    const configuredColumn = columnConfiguration.find((c) => c.id === column.id);
    const name = intl.formatMessage(messages[column.messageDescriptorKey]);
    return {
      ...column,
      ...configuredColumn,
      name,
      minWidth: 70,
    };
  });

  const data = useMemo(() => {
    return batches.map((batch) => ({
      fulfillmentPlanSetId: batch.fulfillmentPlanSetId,
      id: batch.fulfillmentPlanSetId,
    }));
  }, [batches]);
  const displayInformation = useMemo(() => {
    return batches.map((batch) => ({
      id: batch.fulfillmentPlanSetId,
      selected: selectedBatches.map((b) => b.fulfillmentPlanSetId).includes(batch.fulfillmentPlanSetId),
    }));
  }, [batches, selectedBatches]);

  const [stateColumns, setStateColumns] = useState(translatedColumns);

  const onOrderByChanged = useCallback(
    (newColumns) => {
      const sortedCol = newColumns.find((col) => col.sorted !== undefined);
      if (!sortedCol) {
        return;
      }
      const sortBy = `${sortedCol.orderByParam}${sortedCol.sorted === 'ascending' ? 'Asc' : 'Desc'}`;
      dispatch(updateBatchGridSorting(sortBy));
      setStateColumns(newColumns);
    },
    [setStateColumns, dispatch],
  );

  const onSelectedChanged = useCallback(
    (newSelectedIds) => {
      dispatch(setSelectedBatchesByIds(newSelectedIds));
    },
    [dispatch],
  );

  const onColumnsChanged = useCallback(
    (newColumns) => {
      dispatch(saveUserSettingGridConfig('shipments', statusViewId, selectedTab, newColumns));
      setStateColumns(newColumns);
      dispatch(shipmentGridChangeColumns(statusViewId, selectedTab, newColumns));
    },
    [setStateColumns, dispatch, selectedTab],
  );

  return (
    <HooksDisplayGrid
      columns={stateColumns}
      data={data}
      displayInformation={displayInformation}
      onColumnsChanged={onColumnsChanged}
      onOrderByChanged={onOrderByChanged}
      onSelectedChanged={onSelectedChanged}
    />
  );
};

export default memo(BatchGridContainer);
